/** Chaves e hooks de cache. Uma chave por recurso do contrato, nada improvisado. */
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { ERRO_NA_TELA } from './aviso-de-erro';
import { ApiRequestError } from './api';
import type {
  AppointmentTypeUpsert,
  CreateTemplateRequest,
  Appointment,
  Client,
  ClientDetail,
  ClientUpsertRequest,
  ConversationDetail,
  ConversationTag,
  ReviewSendRequest,
  ReviewStatus,
  ReviewTemplateRequest,
  DependentUpsert,
  PatientCreate,
  PatientDetail,
  PatientRecordUpdate,
  PatientSummary,
  ConnectCheck,
  ConnectLink,
  ConversationSummary,
  DashboardStats,
  MonthlyMessageCost,
  Doctor,
  MetaOnboardResult,
  MetaSignupConfig,
  SendMessageRequest,
  Uuid,
  TemplateModel,
  QuickReply,
  QuickReplyUpsert,
  AgendaConfig,
  AgendaDay,
  AgendaIntegration,
  CalendarLinkUpsert,
  PlatformDiscoveryRequest,
  AgendaSettings,
  AvailabilityBulkRequest,
  AvailabilityDay,
  AvailabilityExceptionUpsert,
  AvailabilityRuleUpsert,
  LocationUpsert,
  BookRequest,
  FreeSlotSearch,
  SlotCheckResult,
  SendTemplateRequest,
  PatientAgenda,
  AcceptInviteRequest,
  AttestationText,
  IncomingInvite,
  InboxFocus,
  PerfilUpdate,
  ShareRequest,
  SharePolicyRequest,
  TeamDetail,
  TeamInviteRequest,
  TeamSummary,
  TeamTerm,
  UpdateMemberRequest,
  UpdateShareRequest,
  ActivityEntry,
  AssignRequest,
  ShiftOverrideRequest,
  ShiftRuleRequest,
  TeamShifts,
} from '@zenda/api-contract';
import {
  fetchPatient,
  fetchPatients,
  createPatient,
  createDependent,
  updateDependent,
  fetchReviewStatus,
  createClientTemplate,
  createTag,
  deleteTag,
  fetchClientTemplates,
  sendConversationTemplate,
  sendPatientTemplate,
  searchContacts,
  openPatientConversation,
  fetchTags,
  reviewCreateTemplate,
  reviewSend,
  tagConversation,
  untagConversation,
  updatePatient,
  connectChannel,
  checkConnectLink,
  createConnectLink,
  disconnectChannel,
  fetchMetaConfig,
  logMetaSession,
  onboardChannel,
  createClient,
  fetchAppointments,
  fetchClient,
  fetchClients,
  fetchConversation,
  fetchConversations,
  fetchDoctors,
  fetchStats,
  fetchMonthlyCost,
  markPaymentPaid,
  markPaymentUnpaid,
  reconnectChannel,
  syncChannel,
  fetchTemplateModels,
  saveTemplateModel,
  deleteTemplateModel,
  fetchQuickReplies,
  createQuickReply,
  updateQuickReply,
  deleteQuickReply,
  fetchAgenda,
  fetchAgendaDays,
  fetchAgendaIntegration,
  createCalendarLink,
  deactivateCalendarLink,
  discoverPlatformCalendars,
  simulatePlatformBooking,
  resolveSyncConflict,
  fetchAvailabilityDays,
  saveAgendaSettings,
  saveAvailabilityBulk,
  updateAvailabilityRule,
  deleteAvailabilityRule,
  createAppointmentType,
  createLocation,
  updateAppointmentType,
  updateLocation,
  deactivateAppointmentType,
  deactivateLocation,
  createAvailabilityException,
  deleteAvailabilityException,
  checkSlots,
  fetchFreeSlots,
  bookAppointments,
  fetchPatientAgenda,
  cancelAppointment,
  cancelPatientAgenda,
  confirmAppointment,
  markPresence,
  rescheduleAppointment,
  readConversation,
  reopenConversation,
  resolveConversation,
  sendMessage,
  updateClient,
  acceptInvite,
  archiveTeam,
  createTeam,
  declineInvite,
  fetchAttestation,
  fetchFocus,
  fetchMyInvites,
  fetchTeam,
  fetchTeamTerm,
  fetchTeams,
  inviteToTeam,
  leaveTeam,
  removeMember,
  renameTeam,
  revokeInvite,
  revokeShare,
  saveFocus,
  saveSharePolicy,
  shareDoctors,
  transferLeadership,
  updateMember,
  updatePerfil,
  updateShare,
  addShiftOverride,
  addShiftRule,
  assignConversation,
  fetchClientActivity,
  fetchTeamActivity,
  fetchTeamShifts,
  removeShiftOverride,
  removeShiftRule,
  setAutoAssign,
} from './api';
import { abrirEmbeddedSignup, SignupInterrompido } from './embedded-signup';

/**
 * Polling agora e REDE DE SEGURANCA, nao o caminho normal.
 *
 * Quem avisa e o stream (`lib/stream.ts`): a mensagem aparece em menos de um
 * segundo. Estes intervalos existem para o caso de a conexao cair e a
 * reconexao demorar — sem eles, uma tela com o stream morto ficaria parada
 * parecendo atualizada, que e o pior desfecho possivel num inbox.
 *
 * Por isso subiram de 8/15/60 s para 60/60/120 s: com o stream funcionando,
 * cada tick e trabalho jogado fora, e a query da lista nao e barata.
 */
/**
 * Polling, e nao WebSocket — que e o M4.
 *
 * A atendente deixa a tela aberta o dia inteiro. Sem refetch, uma mensagem que chega
 * fica invisivel ate ela clicar em algo, e o contador da janela de 24 h (computado no
 * servidor) congela no valor de quando a pagina carregou — que e pior do que nao ter
 * contador, porque parece confiavel.
 *
 * Intervalos diferentes por custo: a conversa aberta e o que ela esta olhando AGORA;
 * a lista pode atrasar alguns segundos; o painel e um resumo e nao muda a cada tick.
 * O TanStack nao faz refetch com a aba escondida por padrao, entao isto nao roda a
 * noite inteira num notebook fechado.
 */
const POLL_ABERTA = 60_000;
const POLL_LISTA = 60_000;
const POLL_PAINEL = 120_000;

export const keys = {
  stats: ['stats'] as const,
  monthlyCost: ['monthly-cost'] as const,
  clients: ['clients'] as const,
  client: (id: Uuid) => ['client', id] as const,
  doctors: ['doctors'] as const,
  channels: ['channels'] as const,
  conversations: ['conversations'] as const,
  conversation: (id: Uuid) => ['conversation', id] as const,
  appointments: ['appointments'] as const,
  patients: (clientId: Uuid, q: string, due: boolean) => ['patients', clientId, q, due] as const,
  patient: (clientId: Uuid, contactId: Uuid) => ['patient', clientId, contactId] as const,
  tags: ['tags'] as const,
  reviewStatus: ['review-status'] as const,
  templateModels: ['template-models'] as const,
  quickReplies: ['quick-replies'] as const,
  metaConfig: ['meta-config'] as const,
  agenda: (clientId: Uuid) => ['agenda', clientId] as const,
  availabilityDays: (clientId: Uuid, from: string, to: string) =>
    ['availability-days', clientId, from, to] as const,
  teams: ['teams'] as const,
  team: (id: Uuid) => ['team', id] as const,
  myInvites: ['my-invites'] as const,
  teamTerm: ['team-term'] as const,
  attestation: ['attestation'] as const,
  focus: ['focus'] as const,
  teamShifts: (id: Uuid) => ['team-shifts', id] as const,
  teamActivity: (id: Uuid) => ['team-activity', id] as const,
  clientActivity: (id: Uuid) => ['client-activity', id] as const,
};

export function useStats() {
  return useQuery<DashboardStats>({
    queryKey: keys.stats,
    queryFn: fetchStats,
    refetchInterval: POLL_PAINEL,
  });
}

export function useMonthlyCost(ativo: boolean) {
  return useQuery<MonthlyMessageCost>({
    queryKey: keys.monthlyCost,
    queryFn: fetchMonthlyCost,
    enabled: ativo,
    refetchInterval: POLL_PAINEL,
  });
}

export function useClients() {
  return useQuery<readonly Client[]>({
    queryKey: keys.clients,
    queryFn: fetchClients,
    // A conexao de um canal cai sozinha, sem ninguem clicar em nada — e essa e a tela
    // onde ela descobre. Deixar so no refetch manual e como o medico liga reclamando
    // antes de a atendente saber.
    refetchInterval: POLL_PAINEL,
  });
}

export function useClient(id: Uuid | null) {
  return useQuery<ClientDetail>({
    queryKey: keys.client(id ?? 'none'),
    queryFn: () => fetchClient(id ?? ''),
    enabled: id !== null,
  });
}

/**
 * Marcar a cobranca como recebida muda a lista de clientes e os tiles do painel
 * (receita e cobrancas em atraso) — os tres precisam ser invalidados juntos, senao ela
 * ve o cliente em dia numa tela e em atraso na outra.
 */
export function useMarkPaymentPaid(clientId: Uuid) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (month: string) => markPaymentPaid(clientId, month),
    meta: ERRO_NA_TELA,
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.client(clientId) });
      void client.invalidateQueries({ queryKey: keys.clients });
      void client.invalidateQueries({ queryKey: keys.stats });
    },
  });
}

/** Desfazer a baixa invalida os MESMOS tres lugares: a receita do painel conta em aberto. */
export function useUndoPaymentPaid(clientId: Uuid) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (month: string) => markPaymentUnpaid(clientId, month),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.client(clientId) });
      void client.invalidateQueries({ queryKey: keys.clients });
      void client.invalidateQueries({ queryKey: keys.stats });
    },
  });
}

/**
 * Cadastrar um cliente muda a lista E os tiles do painel (clientes ativos, receita
 * mensal). Invalidar so a lista deixaria a receita defasada ate o proximo refetch —
 * e o numero errado no painel e pior que numero nenhum.
 */
export function useCreateClient() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (payload: ClientUpsertRequest) => createClient(payload),
    meta: ERRO_NA_TELA,
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.clients });
      void client.invalidateQueries({ queryKey: keys.stats });
      void client.invalidateQueries({ queryKey: keys.doctors });
    },
  });
}

/**
 * Editar tambem invalida `stats`: mudar a mensalidade — ou marcar o cliente como
 * encerrado — muda a receita do painel na mesma hora. Numero defasado num tile e pior
 * que numero nenhum, porque ninguem desconfia dele.
 */
export function useUpdateClient(id: Uuid) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (payload: ClientUpsertRequest) => updateClient(id, payload),
    meta: ERRO_NA_TELA,
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.client(id) });
      void client.invalidateQueries({ queryKey: keys.clients });
      void client.invalidateQueries({ queryKey: keys.stats });
    },
  });
}

/**
 * Conectar/reconectar mexe no canal, que aparece em tres lugares: no detalhe do
 * cliente, na coluna "Conexao" da tabela e no contador de canais saudaveis do painel.
 */
export function useConnectChannel(clientId: Uuid) {
  const client = useQueryClient();
  const invalidate = (): void => {
    void client.invalidateQueries({ queryKey: keys.client(clientId) });
    void client.invalidateQueries({ queryKey: keys.clients });
    void client.invalidateQueries({ queryKey: keys.channels });
    void client.invalidateQueries({ queryKey: keys.stats });
  };

  const connect = useMutation({
    mutationFn: (doctorId: Uuid) => connectChannel(doctorId),
    meta: ERRO_NA_TELA,
    onSuccess: invalidate,
  });
  const reconnect = useMutation({
    mutationFn: (channelId: Uuid) => reconnectChannel(channelId),
    meta: ERRO_NA_TELA,
    onSuccess: invalidate,
  });
  return { connect, reconnect };
}

/**
 * O link que a secretaria manda para o medico.
 *
 * Sem invalidar cache nenhum de proposito: gerar um link nao muda o estado de conexao de
 * ninguem. O que muda o estado e o medico abrir e autorizar, e isso chega por outra via.
 */
/**
 * Conferir com a Gupshup se o médico já terminou. O canal novo aparece nas telas do
 * cliente, então as listas são relidas quando ele nasce.
 */
export function useConnectCheck(clientId: Uuid) {
  const client = useQueryClient();
  return useMutation<ConnectCheck, Error, Uuid>({
    mutationFn: (doctorId) => checkConnectLink(doctorId),
    meta: ERRO_NA_TELA,
    onSuccess: (r) => {
      if (r.estado !== 'ativado' && r.estado !== 'ja_ativo') return;
      void client.invalidateQueries({ queryKey: keys.client(clientId) });
      void client.invalidateQueries({ queryKey: keys.clients });
    },
  });
}

export function useConnectLink() {
  return useMutation<ConnectLink, Error, Uuid>({
    mutationFn: (doctorId) => createConnectLink(doctorId),
    meta: ERRO_NA_TELA,
  });
}

/**
 * `available: false` e a resposta honesta de um ambiente sem a integracao ligada. A tela
 * usa isso para mostrar o roteiro em vez de um botao que abre um fluxo quebrado.
 */
export function useMetaConfig() {
  return useQuery<MetaSignupConfig>({
    queryKey: keys.metaConfig,
    queryFn: fetchMetaConfig,
    staleTime: Infinity,
  });
}

/**
 * O Embedded Signup de ponta a ponta: abre a janela da Meta, acompanha o funil e troca o
 * `code` no servidor.
 *
 * O `code` vive 30 s ✅, entao a troca acontece no mesmo gesto — sem confirmacao
 * intermediaria, sem fila e sem retry com backoff, que perderiam a janela por construcao.
 */
export function useEmbeddedSignup(clientId: Uuid) {
  const client = useQueryClient();
  const invalidate = (): void => {
    void client.invalidateQueries({ queryKey: keys.client(clientId) });
    void client.invalidateQueries({ queryKey: keys.clients });
    void client.invalidateQueries({ queryKey: keys.channels });
    void client.invalidateQueries({ queryKey: keys.stats });
  };

  return useMutation<
    MetaOnboardResult,
    Error,
    {
      readonly doctorId: Uuid;
      readonly config: MetaSignupConfig;
      readonly onStep?: (step: string) => void;
    }
  >({
    meta: ERRO_NA_TELA,
    mutationFn: async ({ doctorId, config, onStep }) => {
      if (!config.available || config.appId === null || config.configId === null) {
        throw new Error('A integracao real com a Meta nao esta ligada neste ambiente.');
      }

      void logMetaSession({ doctorId, clientId, status: 'started' });

      let resultado;
      try {
        resultado = await abrirEmbeddedSignup({
          appId: config.appId,
          configId: config.configId,
          graphVersion: config.graphVersion,
          aoAndar: (passo) => {
            if (passo.tipo !== 'passo') return;
            onStep?.(passo.step);
            void logMetaSession({
              doctorId,
              clientId,
              status: 'started',
              currentStep: passo.step,
              ...(passo.sessionId === null ? {} : { sessionId: passo.sessionId }),
            });
          },
        });
      } catch (err) {
        const parada = err instanceof SignupInterrompido ? err : null;
        void logMetaSession({
          doctorId,
          clientId,
          status: parada?.motivo === 'cancelado' ? 'cancelled' : 'failed',
          errorMessage: (err as Error).message,
          ...(parada?.step == null ? {} : { currentStep: parada.step }),
          ...(parada?.errorCode == null ? {} : { errorCode: parada.errorCode }),
          ...(parada?.sessionId == null ? {} : { sessionId: parada.sessionId }),
        });
        throw err;
      }

      void logMetaSession({
        doctorId,
        clientId,
        status: 'finished',
        wabaId: resultado.wabaId,
        phoneNumberId: resultado.phoneNumberId,
        ...(resultado.businessId === null ? {} : { businessId: resultado.businessId }),
        ...(resultado.sessionId === null ? {} : { sessionId: resultado.sessionId }),
      });

      return onboardChannel({
        code: resultado.code,
        wabaId: resultado.wabaId,
        phoneNumberId: resultado.phoneNumberId,
        doctorId,
        modo: resultado.modo,
        ...(resultado.sessionId === null ? {} : { sessionId: resultado.sessionId }),
      });
    },
    onSuccess: invalidate,
  });
}

/**
 * Confere qualidade e limite com a Meta.
 *
 * A conferencia automatica, ao abrir a tela, falha calada: se a Graph API estiver
 * fora, o cartao segue mostrando o ultimo valor conferido e a data dele, que continua
 * sendo verdade. O clique em "Conferir agora" avisa, porque ali alguem pediu.
 */
export function useDisconnectChannel(clientId: Uuid) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (channelId: Uuid) => disconnectChannel(channelId),
    meta: ERRO_NA_TELA,
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.client(clientId) });
      void client.invalidateQueries({ queryKey: keys.clients });
    },
  });
}

export function useSyncChannel(clientId: Uuid) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (channelId: Uuid) => syncChannel(channelId),
    meta: ERRO_NA_TELA,
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.client(clientId) });
      void client.invalidateQueries({ queryKey: keys.clients });
    },
  });
}

export function useDoctors() {
  return useQuery<readonly Doctor[]>({ queryKey: keys.doctors, queryFn: fetchDoctors });
}

/**
 * A lista do inbox, com o foco salvo, ou todos os médicos (`todos`).
 *
 * A chave começa por `conversations` nos dois casos, para quem invalida a lista
 * (stream, envio) alcançar as duas variantes de uma vez.
 */
export function useConversations(todos = false) {
  return useQuery<readonly ConversationSummary[]>({
    queryKey: todos ? [...keys.conversations, 'todos'] : keys.conversations,
    queryFn: () => fetchConversations(todos),
    refetchInterval: POLL_LISTA,
  });
}

export function useConversation(id: Uuid | null) {
  return useQuery<ConversationDetail>({
    queryKey: keys.conversation(id ?? 'none'),
    queryFn: () => fetchConversation(id ?? ''),
    enabled: id !== null,
    refetchInterval: POLL_ABERTA,
    // 404 é a conversa que saiu do alcance (médico retirado da equipe): tentar de novo
    // só adia a tela que diz isso.
    retry: (vezes, erro) => !(erro instanceof ApiRequestError && erro.status === 404) && vezes < 3,
  });
}

export function useAppointments() {
  return useQuery<readonly Appointment[]>({
    queryKey: keys.appointments,
    queryFn: fetchAppointments,
  });
}

export function useSendMessage(conversationId: Uuid) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (payload: SendMessageRequest) => sendMessage(conversationId, payload),
    meta: ERRO_NA_TELA,
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.conversation(conversationId) });
      void client.invalidateQueries({ queryKey: keys.conversations });
      void client.invalidateQueries({ queryKey: keys.stats });
    },
  });
}

/**
 * Abrir a conversa zera o badge.
 *
 * O contador cai na tela ANTES da resposta do servidor, mexendo direto no cache
 * da lista. Sem isso a badge some 15 segundos depois — no proximo polling — e
 * nesse meio tempo a atendente ja abriu, leu e voltou, vendo o numero ainda la.
 * Na leitura nao ha o que dar errado do lado do usuario: ela leu.
 */
export function useReadConversation() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (conversationId: Uuid) => readConversation(conversationId),
    onMutate: async (conversationId: Uuid) => {
      /**
       * Cancelar ANTES de escrever.
       *
       * A mensagem nova chega por SSE, que invalida a lista: quando a marcacao
       * de leitura sai, esse refetch costuma estar em voo e leu o contador
       * ANTIGO. O `invalidateQueries` do `onSuccess` cai na deduplicacao do
       * React Query — ele nao dispara busca nova, adota a que ja corre — e o
       * cache termina com o valor de antes da leitura. O badge fica em 1 ate a
       * proxima busca por outro motivo, que na pratica e o clique da atendente.
       */
      await client.cancelQueries({ queryKey: keys.conversations });
      client.setQueryData<readonly ConversationSummary[]>(keys.conversations, (atual) =>
        atual?.map((c) => (c.id === conversationId ? { ...c, unreadCount: 0 } : c)),
      );
    },
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.conversations });
    },
  });
}

/**
 * Volta a conversa para a fila. Invalida os mesmos tres lugares que resolver —
 * se um deles ficasse para tras, a mesma conversa apareceria resolvida numa
 * tela e aberta na outra.
 */
export function useReopenConversation(conversationId: Uuid) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () => reopenConversation(conversationId),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.conversation(conversationId) });
      void client.invalidateQueries({ queryKey: keys.conversations });
      void client.invalidateQueries({ queryKey: keys.stats });
    },
  });
}

export function useResolveConversation(conversationId: Uuid) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () => resolveConversation(conversationId),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.conversation(conversationId) });
      void client.invalidateQueries({ queryKey: keys.conversations });
      void client.invalidateQueries({ queryKey: keys.stats });
    },
  });
}

// ── Pacientes ───────────────────────────────────────────────────────────────

export function usePatients(clientId: Uuid | null, q = '', due = false, ativo = true) {
  return useQuery<readonly PatientSummary[]>({
    queryKey: keys.patients(clientId ?? 'none', q, due),
    queryFn: () => fetchPatients(clientId ?? '', { q, due }),
    enabled: clientId !== null && ativo,
    // A lista anterior fica na tela enquanto a busca nova carrega. Sem isso, cada
    // tecla digitada pisca a tabela inteira para um esqueleto — e a pessoa perde a
    // referencia do que estava lendo.
    placeholderData: (anterior) => anterior,
  });
}

export function usePatient(clientId: Uuid | null, contactId: Uuid | null) {
  return useQuery<PatientDetail>({
    queryKey: keys.patient(clientId ?? 'none', contactId ?? 'none'),
    queryFn: () => fetchPatient(clientId ?? '', contactId ?? ''),
    enabled: clientId !== null && contactId !== null,
  });
}

/**
 * Salvar a ficha invalida a LISTA tambem: o intervalo de retorno muda a ordem da
 * tabela (ordenada por quem volta primeiro) e a etiqueta de vencimento. Invalidar so a
 * ficha deixaria a lista contando outra historia sobre o mesmo paciente.
 */
export function usePatientMutations(clientId: Uuid, contactId: Uuid) {
  const client = useQueryClient();
  const invalidate = (): void => {
    void client.invalidateQueries({ queryKey: keys.patient(clientId, contactId) });
    void client.invalidateQueries({ queryKey: ['patients', clientId] });
    // Nome e telefone do paciente aparecem também no inbox.
    void client.invalidateQueries({ queryKey: keys.conversations });
    void client.invalidateQueries({ queryKey: ['conversation'] });
  };

  const save = useMutation({
    mutationFn: (payload: PatientRecordUpdate) => updatePatient(clientId, contactId, payload),
    onSuccess: invalidate,
  });
  const criarDependente = useMutation({
    mutationFn: (payload: DependentUpsert) => createDependent(clientId, contactId, payload),
    onSuccess: invalidate,
  });
  const editarDependente = useMutation({
    mutationFn: ({ id, ...payload }: DependentUpsert & { readonly id: Uuid }) =>
      updateDependent(clientId, contactId, id, payload),
    onSuccess: () => {
      invalidate();
      // O nome do dependente aparece nos cartões da agenda, na ficha do inbox e no painel.
      void client.invalidateQueries({ queryKey: ['agenda-days'] });
      void client.invalidateQueries({ queryKey: ['patient-agenda', contactId] });
      void client.invalidateQueries({ queryKey: keys.appointments });
    },
  });
  return { save, criarDependente, editarDependente };
}

export function useCreatePatient(clientId: Uuid) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (payload: PatientCreate) => createPatient(clientId, payload),
    onSuccess: () => void client.invalidateQueries({ queryKey: ['patients', clientId] }),
  });
}

// ── Tags ────────────────────────────────────────────────────────────────────

/**
 * As etiquetas da casa, ou as da operação dona de uma conversa.
 *
 * Com equipe, a conversa do médico compartilhado só aceita etiqueta da operação dele
 * (docs/domain/equipes.md §5): a tela da conversa pede as daquela conversa.
 */
export function useTags(conversationId?: Uuid) {
  return useQuery<readonly ConversationTag[]>({
    queryKey: conversationId === undefined ? keys.tags : [...keys.tags, conversationId],
    queryFn: () => fetchTags(conversationId),
  });
}

export function useConversationTags(conversationId: Uuid) {
  const client = useQueryClient();
  const invalidate = (): void => {
    void client.invalidateQueries({ queryKey: keys.conversation(conversationId) });
    void client.invalidateQueries({ queryKey: keys.conversations });
    void client.invalidateQueries({ queryKey: keys.tags });
  };
  const add = useMutation({
    mutationFn: (p: { tagId?: Uuid; name?: string; color?: string }) =>
      tagConversation(conversationId, p),
    onSuccess: invalidate,
  });
  const remove = useMutation({
    mutationFn: (tagId: Uuid) => untagConversation(conversationId, tagId),
    onSuccess: invalidate,
  });
  return { add, remove };
}

// ── Console de verificacao ──────────────────────────────────────────────────

/**
 * Criar e apagar etiquetas.
 *
 * Invalida `tags` E `conversations`: apagar uma etiqueta remove o vinculo dela
 * com as conversas por cascade no banco, e a lista do inbox mostra essas
 * etiquetas. Sem a segunda invalidacao a etiqueta apagada continuaria pintada
 * nas linhas ate alguem recarregar a pagina.
 */
export function useTagMutations() {
  const qc = useQueryClient();
  const invalidar = (): void => {
    void qc.invalidateQueries({ queryKey: keys.tags });
    void qc.invalidateQueries({ queryKey: keys.conversations });
  };

  return {
    create: useMutation({
      mutationFn: (payload: { name: string; color?: string }) => createTag(payload),
      meta: ERRO_NA_TELA,
      onSuccess: invalidar,
    }),
    remove: useMutation({
      mutationFn: (id: Uuid) => deleteTag(id),
      onSuccess: invalidar,
    }),
  };
}

/**
 * Templates de um cliente. `staleTime` curto de proposito: o status vem da Meta
 * e muda sem avisar — mostrar "em analise" quando ja foi aprovado e o tipo de
 * mentira que faz alguem esperar sem motivo.
 */
/**
 * Enviar template dentro da conversa.
 *
 * Invalida a conversa E a lista: o envio reabre a janela de 24 h, e a lista
 * mostra esse estado em cada linha.
 */
export function useSendTemplate(conversationId: Uuid | null) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (payload: SendTemplateRequest) =>
      sendConversationTemplate(conversationId as Uuid, payload),
    meta: ERRO_NA_TELA,
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.conversation(conversationId ?? 'none') });
      void client.invalidateQueries({ queryKey: keys.conversations });
    },
  });
}

/** O primeiro template para um paciente, que pode ainda não ter conversa (ADR 0028). */
export function useSendPatientTemplate(clientId: Uuid, contactId: Uuid | null) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (payload: SendTemplateRequest) =>
      sendPatientTemplate(clientId, contactId as Uuid, payload),
    meta: ERRO_NA_TELA,
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['patients', clientId] });
      void client.invalidateQueries({ queryKey: ['patient', clientId] });
      void client.invalidateQueries({ queryKey: keys.conversations });
    },
  });
}

export function useClientTemplates(clientId: Uuid | null) {
  return useQuery({
    queryKey: ['client-templates', clientId] as const,
    queryFn: () => fetchClientTemplates(clientId as Uuid),
    enabled: clientId !== null,
    staleTime: 15_000,
  });
}

/**
 * Cria o template e recarrega a lista.
 *
 * Nao ha atualizacao otimista: o template nasce `PENDING` do lado da Meta, e
 * fingir que ja existe aprovado seria mentir sobre a unica coisa que importa
 * saber dele.
 */
export function useCreateTemplate(clientId: Uuid) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateTemplateRequest) => createClientTemplate(clientId, payload),
    meta: ERRO_NA_TELA,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['client-templates', clientId] });
    },
  });
}

export function useReviewStatus() {
  return useQuery<ReviewStatus>({
    queryKey: keys.reviewStatus,
    queryFn: fetchReviewStatus,
  });
}

/**
 * As duas chamadas nao invalidam nada.
 *
 * O que elas produzem — uma mensagem no WhatsApp de alguem e um template na
 * conta da Meta — nao esta em nenhum cache nosso, e fingir que esta faria a
 * tela mentir sobre o proprio efeito.
 */
export function useReviewActions() {
  const send = useMutation({
    mutationFn: (payload: ReviewSendRequest) => reviewSend(payload),
    meta: ERRO_NA_TELA,
  });
  const createTemplate = useMutation({
    mutationFn: (payload: ReviewTemplateRequest) => reviewCreateTemplate(payload),
    meta: ERRO_NA_TELA,
  });
  return { send, createTemplate };
}

// ── Biblioteca e respostas prontas ──────────────────────────────────────────

export function useTemplateModels() {
  return useQuery<readonly TemplateModel[]>({
    queryKey: keys.templateModels,
    queryFn: fetchTemplateModels,
  });
}

export function useTemplateModelMutations() {
  const client = useQueryClient();
  const invalidar = (): void => {
    void client.invalidateQueries({ queryKey: keys.templateModels });
  };
  return {
    save: useMutation({ mutationFn: saveTemplateModel, onSuccess: invalidar, meta: ERRO_NA_TELA }),
    remove: useMutation({ mutationFn: deleteTemplateModel, onSuccess: invalidar }),
  };
}

export function useQuickReplies() {
  return useQuery<readonly QuickReply[]>({
    queryKey: keys.quickReplies,
    queryFn: fetchQuickReplies,
  });
}

export function useQuickReplyMutations() {
  const client = useQueryClient();
  const invalidar = (): void => {
    void client.invalidateQueries({ queryKey: keys.quickReplies });
  };
  return {
    create: useMutation({ mutationFn: createQuickReply, onSuccess: invalidar }),
    update: useMutation({
      mutationFn: ({ id, payload }: { id: Uuid; payload: QuickReplyUpsert }) =>
        updateQuickReply(id, payload),
      onSuccess: invalidar,
    }),
    remove: useMutation({ mutationFn: deleteQuickReply, onSuccess: invalidar }),
  };
}

// ── Agenda ──────────────────────────────────────────────────────────────────

export function useAgenda(clientId: Uuid) {
  return useQuery<AgendaConfig>({
    queryKey: keys.agenda(clientId),
    queryFn: () => fetchAgenda(clientId),
    enabled: clientId !== '',
  });
}

export function useAvailabilityDays(clientId: Uuid, from: string, to: string) {
  return useQuery<readonly AvailabilityDay[]>({
    queryKey: keys.availabilityDays(clientId, from, to),
    queryFn: () => fetchAvailabilityDays(clientId, from, to),
  });
}

/**
 * A agenda de um médico numa janela de dias.
 *
 * `refetchInterval` como a lista de conversas: a atendente deixa a semana aberta
 * enquanto atende, e uma consulta marcada pela colega, ou por um paciente numa
 * plataforma de terceiro, não aparece por si — não há evento de agenda no
 * `/api/stream`, que só publica conversa.
 */
export function useAgendaDays(
  clientId: Uuid | null,
  doctorId: Uuid | null,
  from: string,
  to: string,
) {
  return useQuery<readonly AgendaDay[]>({
    queryKey: ['agenda-days', clientId ?? 'none', doctorId ?? 'none', from, to],
    queryFn: () => fetchAgendaDays(clientId ?? '', doctorId ?? '', from, to),
    enabled: clientId !== null && doctorId !== null,
    refetchInterval: POLL_LISTA,
  });
}

/**
 * Toda mutacao de agenda invalida a configuracao E os dias resolvidos: mudar uma
 * regra muda quais horarios existem, e deixar a grade do calendario com o
 * calculo antigo e como a atendente oferece um horario que nao existe mais.
 */
export function useAgendaMutations(clientId: Uuid) {
  const client = useQueryClient();
  const invalidar = (): void => {
    void client.invalidateQueries({ queryKey: keys.agenda(clientId) });
    void client.invalidateQueries({ queryKey: ['availability-days', clientId] });
  };
  return {
    salvarGrade: useMutation({
      mutationFn: (payload: AvailabilityBulkRequest) => saveAvailabilityBulk(clientId, payload),
      onSuccess: invalidar,
    }),
    atualizarRegra: useMutation({
      mutationFn: ({ id, payload }: { id: Uuid; payload: AvailabilityRuleUpsert }) =>
        updateAvailabilityRule(clientId, id, payload),
      onSuccess: invalidar,
    }),
    apagarRegra: useMutation({
      mutationFn: (id: Uuid) => deleteAvailabilityRule(clientId, id),
      onSuccess: invalidar,
    }),
    criarTipo: useMutation({
      mutationFn: (payload: AppointmentTypeUpsert) => createAppointmentType(clientId, payload),
      onSuccess: invalidar,
    }),
    atualizarTipo: useMutation({
      mutationFn: ({ id, payload }: { id: Uuid; payload: AppointmentTypeUpsert }) =>
        updateAppointmentType(clientId, id, payload),
      onSuccess: invalidar,
    }),
    desativarTipo: useMutation({
      mutationFn: (id: Uuid) => deactivateAppointmentType(clientId, id),
      onSuccess: invalidar,
    }),
    criarLocal: useMutation({
      mutationFn: (payload: LocationUpsert) => createLocation(clientId, payload),
      onSuccess: invalidar,
    }),
    atualizarLocal: useMutation({
      mutationFn: ({ id, payload }: { id: Uuid; payload: LocationUpsert }) =>
        updateLocation(clientId, id, payload),
      onSuccess: invalidar,
    }),
    desativarLocal: useMutation({
      mutationFn: (id: Uuid) => deactivateLocation(clientId, id),
      onSuccess: invalidar,
    }),
    criarExcecao: useMutation({
      mutationFn: (payload: AvailabilityExceptionUpsert) =>
        createAvailabilityException(clientId, payload),
      onSuccess: invalidar,
    }),
    apagarExcecao: useMutation({
      mutationFn: (id: Uuid) => deleteAvailabilityException(clientId, id),
      onSuccess: invalidar,
    }),
    salvarPolitica: useMutation({
      mutationFn: (payload: Partial<AgendaSettings>) => saveAgendaSettings(clientId, payload),
      onSuccess: invalidar,
    }),
  };
}

// ── Integração com plataforma de terceiro ───────────────────────────────────

export function useAgendaIntegration(clientId: Uuid | null) {
  return useQuery<AgendaIntegration>({
    queryKey: ['agenda-integration', clientId ?? 'none'],
    queryFn: () => fetchAgendaIntegration(clientId ?? ''),
    enabled: clientId !== null,
  });
}

/**
 * Toda ação de sincronia invalida a integração E os dias.
 *
 * Publicar não muda a agenda daqui, mas puxar e simular MUDAM: uma reserva que
 * chega de fora é uma consulta nova. Invalidar só a integração deixaria a grade
 * mostrando o horário livre que a paciente acabou de tomar — foi exatamente o
 * defeito que a marcação pela agenda teve antes de `agenda-days` entrar na lista.
 */
export function useIntegrationMutations(clientId: Uuid) {
  const client = useQueryClient();
  const invalidar = (): void => {
    void client.invalidateQueries({ queryKey: ['agenda-integration', clientId] });
    void client.invalidateQueries({ queryKey: ['agenda-days'] });
    void client.invalidateQueries({ queryKey: ['free-slots'] });
  };
  return {
    vincular: useMutation({
      mutationFn: (payload: CalendarLinkUpsert) => createCalendarLink(clientId, payload),
      onSuccess: invalidar,
    }),
    desvincular: useMutation({
      mutationFn: (id: Uuid) => deactivateCalendarLink(clientId, id),
      onSuccess: invalidar,
    }),
    /**
     * Le os calendarios da plataforma para mapear. NAO invalida nada: e leitura, e
     * o resultado vive no estado da janela de configuracao ate alguem salvar.
     */
    descobrir: useMutation({
      mutationFn: (p: PlatformDiscoveryRequest) => discoverPlatformCalendars(clientId, p),
    }),
    simular: useMutation({
      mutationFn: ({
        id,
        ...p
      }: {
        id: Uuid;
        date: string;
        hora: string;
        patientName?: string;
        patientPhone?: string;
      }) => simulatePlatformBooking(clientId, id, p),
      onSuccess: invalidar,
    }),
    resolverConflito: useMutation({
      mutationFn: (id: Uuid) => resolveSyncConflict(clientId, id),
      onSuccess: invalidar,
    }),
  };
}

// ── Marcação ────────────────────────────────────────────────────────────────

export function useFreeSlots(
  clientId: Uuid,
  q: {
    durationMin: number;
    from?: string;
    weekdays?: readonly number[];
    periodo?: 'manha' | 'tarde' | 'qualquer';
    limit?: number;
    overbook?: boolean;
  },
  ativo: boolean,
) {
  return useQuery<FreeSlotSearch>({
    queryKey: ['free-slots', clientId, q],
    queryFn: () => fetchFreeSlots(clientId, q),
    enabled: ativo,
    // Horário livre azeda: entre abrir o painel e escolher, outra pessoa marca.
    staleTime: 30_000,
  });
}

/**
 * O veredito de cada data da serie, direto do servidor.
 *
 * A tela nao consegue responder isso sozinha: a busca devolve uma janela, e a
 * 5a sessao de uma serie semanal cai bem depois do fim dela.
 */
export function useSlotCheck(
  clientId: Uuid,
  durationMin: number,
  locationId: Uuid | null,
  slots: readonly { date: string; hora: string }[],
) {
  return useQuery<SlotCheckResult>({
    queryKey: ['slot-check', clientId, durationMin, locationId, slots],
    queryFn: () => checkSlots(clientId, durationMin, locationId, slots),
    enabled: slots.length > 0 && durationMin > 0,
    staleTime: 30_000,
  });
}

/** Abre (ou cria vazia) a conversa do paciente com o médico. */
export function useAbrirConversa() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ clientId, contactId }: { clientId: Uuid; contactId: Uuid }) =>
      openPatientConversation(clientId, contactId),
    meta: ERRO_NA_TELA,
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.conversations });
      void client.invalidateQueries({ queryKey: ['patients'] });
      void client.invalidateQueries({ queryKey: ['patient'] });
    },
  });
}

export function useContactSearch(q: string) {
  const termo = q.trim();
  return useQuery({
    queryKey: ['contact-search', termo] as const,
    queryFn: () => searchContacts(termo),
    enabled: termo.length >= 2,
    staleTime: 10_000,
  });
}

export function usePatientAgenda(contactId: Uuid | null) {
  return useQuery<PatientAgenda>({
    queryKey: ['patient-agenda', contactId ?? 'none'],
    queryFn: () => fetchPatientAgenda(contactId ?? ''),
    enabled: contactId !== null,
  });
}

/**
 * Marcar mexe em tudo o que fala de agenda: a lista de livres, a agenda do
 * paciente, a agenda do médico, e a própria conversa — que mostra a próxima
 * consulta no cabeçalho.
 *
 * `agenda-days` entrou depois e a falta dele foi vista na tela: marcar a partir da
 * agenda gravava a consulta e a grade continuava mostrando o horário livre. Uma
 * lista de chaves aqui é a única coisa que amarra a escrita ao que está desenhado.
 */
/**
 * O que se faz com UMA consulta ja marcada: remarcar, confirmar, registrar se veio,
 * desmarcar. Em cliente com agenda de terceiro cada acao vai para la tambem.
 */
export function useAcoesDaConsulta() {
  const client = useQueryClient();
  const invalidar = (): void => {
    for (const k of ['agenda-days', 'free-slots', 'slot-check', 'patient-agenda']) {
      void client.invalidateQueries({ queryKey: [k] });
    }
  };
  return {
    remarcar: useMutation({
      mutationFn: (p: { id: Uuid; date: string; hora: string; durationMin?: number }) =>
        rescheduleAppointment(p.id, {
          date: p.date,
          hora: p.hora,
          ...(p.durationMin === undefined ? {} : { durationMin: p.durationMin }),
        }),
      meta: ERRO_NA_TELA,
      onSuccess: invalidar,
    }),
    confirmar: useMutation({
      mutationFn: (id: Uuid) => confirmAppointment(id),
      meta: ERRO_NA_TELA,
      onSuccess: invalidar,
    }),
    presenca: useMutation({
      mutationFn: (p: { id: Uuid; veio: boolean }) => markPresence(p.id, p.veio),
      meta: ERRO_NA_TELA,
      onSuccess: invalidar,
    }),
    desmarcar: useMutation({
      mutationFn: (id: Uuid) => cancelAppointment(id),
      meta: ERRO_NA_TELA,
      onSuccess: invalidar,
    }),
  };
}

export function useBooking(contactId: Uuid | null, conversationId: Uuid | null, clientId?: Uuid) {
  const client = useQueryClient();
  const invalidar = (): void => {
    void client.invalidateQueries({ queryKey: ['free-slots'] });
    void client.invalidateQueries({ queryKey: ['slot-check'] });
    void client.invalidateQueries({ queryKey: ['agenda-days'] });
    void client.invalidateQueries({ queryKey: ['patient-agenda', contactId ?? 'none'] });
    void client.invalidateQueries({ queryKey: keys.conversations });
    if (conversationId !== null) {
      void client.invalidateQueries({ queryKey: keys.conversation(conversationId) });
    }
  };
  return {
    marcar: useMutation({
      mutationFn: (p: BookRequest) => bookAppointments(p),
      meta: ERRO_NA_TELA,
      onSuccess: invalidar,
    }),
    desmarcar: useMutation({
      mutationFn: ({ id, serie }: { id: Uuid; serie?: boolean }) => cancelAppointment(id, serie),
      onSuccess: invalidar,
    }),
    desmarcarTudo: useMutation({
      mutationFn: () => cancelPatientAgenda(contactId ?? '', clientId),
      onSuccess: invalidar,
    }),
  };
}

// ── Equipes (docs/domain/equipes.md) ─────────────────────────────────────────

/**
 * Tudo o que muda quando o acesso muda: entrar, sair, compartilhar, revogar.
 * O inbox, os clientes, a agenda e o painel passam a mostrar outra coisa.
 */
export function invalidarAcesso(client: ReturnType<typeof useQueryClient>): void {
  for (const k of [
    keys.teams,
    keys.myInvites,
    keys.clients,
    keys.conversations,
    keys.doctors,
    keys.stats,
    keys.tags,
    keys.quickReplies,
    keys.focus,
  ]) {
    void client.invalidateQueries({ queryKey: k });
  }
  void client.invalidateQueries({ queryKey: ['team'] });
}

export function useTeams() {
  return useQuery<readonly TeamSummary[]>({ queryKey: keys.teams, queryFn: fetchTeams });
}

/**
 * O nome de cada pessoa das minhas equipes, por id. O stream manda só ids (equipes.md
 * §7.2, §13.10): o nome é resolvido aqui, pela mesma leitura que a tela de equipe faz.
 */
export function useNomesDaEquipe(): ReadonlyMap<string, string> {
  const equipes = useTeams();
  const detalhes = useQueries({
    queries: (equipes.data ?? []).map((e) => ({
      queryKey: keys.team(e.id),
      queryFn: () => fetchTeam(e.id),
    })),
  });
  return useMemo(() => {
    const nomes = new Map<string, string>();
    for (const d of detalhes) for (const m of d.data?.memberList ?? []) nomes.set(m.userId, m.name);
    return nomes;
  }, [detalhes]);
}

export function useTeam(id: Uuid | null) {
  return useQuery<TeamDetail>({
    queryKey: keys.team(id ?? 'none'),
    queryFn: () => fetchTeam(id ?? ''),
    enabled: id !== null,
  });
}

export function useMyInvites() {
  return useQuery<readonly IncomingInvite[]>({ queryKey: keys.myInvites, queryFn: fetchMyInvites });
}

export function useTeamTerm(enabled: boolean) {
  return useQuery<TeamTerm>({ queryKey: keys.teamTerm, queryFn: fetchTeamTerm, enabled });
}

export function useAttestation(enabled: boolean) {
  return useQuery<AttestationText>({
    queryKey: keys.attestation,
    queryFn: fetchAttestation,
    enabled,
  });
}

/** Toda mutação de equipe muda o acesso de alguém: invalida o mesmo conjunto. */
function useMutacaoDeEquipe<V, R>(fn: (v: V) => Promise<R>, depois?: () => void) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      invalidarAcesso(client);
      depois?.();
    },
  });
}

export function useTeamMutations(teamId: Uuid | null, aoMudar?: () => void) {
  const id = teamId ?? '';
  return {
    rename: useMutacaoDeEquipe((name: string) => renameTeam(id, { name }), aoMudar),
    archive: useMutacaoDeEquipe(() => archiveTeam(id), aoMudar),
    leave: useMutacaoDeEquipe(() => leaveTeam(id), aoMudar),
    transfer: useMutacaoDeEquipe((userId: Uuid) => transferLeadership(id, userId), aoMudar),
    invite: useMutacaoDeEquipe((b: TeamInviteRequest) => inviteToTeam(id, b), aoMudar),
    revokeInvite: useMutacaoDeEquipe((inviteId: Uuid) => revokeInvite(id, inviteId), aoMudar),
    setRole: useMutacaoDeEquipe(
      (b: { userId: Uuid; role: UpdateMemberRequest['role'] }) =>
        updateMember(id, b.userId, { role: b.role }),
      aoMudar,
    ),
    removeMember: useMutacaoDeEquipe((userId: Uuid) => removeMember(id, userId), aoMudar),
    share: useMutacaoDeEquipe((b: ShareRequest) => shareDoctors(id, b), aoMudar),
    updateShare: useMutacaoDeEquipe(
      (b: { clientId: Uuid } & UpdateShareRequest) =>
        updateShare(id, b.clientId, {
          access: b.access,
          ...(b.attestationTextId === undefined ? {} : { attestationTextId: b.attestationTextId }),
        }),
      aoMudar,
    ),
    revokeShare: useMutacaoDeEquipe((clientId: Uuid) => revokeShare(id, clientId), aoMudar),
    sharePolicy: useMutacaoDeEquipe((b: SharePolicyRequest) => saveSharePolicy(id, b), aoMudar),
  };
}

export function useCreateTeam(aoCriar?: (id: Uuid) => void) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (name: string) => createTeam({ name }),
    onSuccess: (r) => {
      invalidarAcesso(client);
      aoCriar?.(r.id);
    },
  });
}

export function useInviteResponses(aoMudar?: () => void) {
  return {
    accept: useMutacaoDeEquipe(
      (b: { inviteId: Uuid } & AcceptInviteRequest) =>
        acceptInvite(b.inviteId, { termId: b.termId, termSha256: b.termSha256 }),
      aoMudar,
    ),
    decline: useMutacaoDeEquipe(
      (b: { inviteId: Uuid; block: boolean }) => declineInvite(b.inviteId, { block: b.block }),
      aoMudar,
    ),
  };
}

export function useFocus() {
  return useQuery<InboxFocus>({ queryKey: keys.focus, queryFn: fetchFocus });
}

/**
 * Salva o foco no servidor: duas abas e o celular concordam sobre o que toca
 * (docs/domain/equipes.md §7.1).
 */
export function useSaveFocus() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (doctorIds: readonly Uuid[] | null) => saveFocus({ doctorIds }),
    onMutate: async (doctorIds) => {
      await client.cancelQueries({ queryKey: keys.focus });
      client.setQueryData<InboxFocus>(keys.focus, { doctorIds });
    },
    onSettled: () => {
      void client.invalidateQueries({ queryKey: keys.focus });
      void client.invalidateQueries({ queryKey: keys.conversations });
      void client.invalidateQueries({ queryKey: keys.stats });
      void client.invalidateQueries({ queryKey: keys.appointments });
    },
  });
}

export function useUpdatePerfil(aoSalvar?: () => void) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (b: PerfilUpdate) => updatePerfil(b),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['conversation'] });
      aoSalvar?.();
    },
  });
}

export function useTeamShifts(teamId: Uuid) {
  return useQuery<TeamShifts>({
    queryKey: keys.teamShifts(teamId),
    queryFn: () => fetchTeamShifts(teamId),
    // "De plantão agora" muda com o relógio, sem ninguém mexer em nada.
    refetchInterval: 60_000,
  });
}

export function useShiftMutations(teamId: Uuid) {
  const client = useQueryClient();
  const invalidate = (): void => {
    void client.invalidateQueries({ queryKey: keys.teamShifts(teamId) });
  };
  return {
    addRule: useMutation({
      mutationFn: (b: ShiftRuleRequest) => addShiftRule(teamId, b),
      onSuccess: invalidate,
    }),
    removeRule: useMutation({
      mutationFn: (shiftId: Uuid) => removeShiftRule(teamId, shiftId),
      onSuccess: invalidate,
    }),
    addOverride: useMutation({
      mutationFn: (b: ShiftOverrideRequest) => addShiftOverride(teamId, b),
      onSuccess: invalidate,
    }),
    removeOverride: useMutation({
      mutationFn: (overrideId: Uuid) => removeShiftOverride(teamId, overrideId),
      onSuccess: invalidate,
    }),
    autoAssign: useMutation({
      mutationFn: (v: boolean) => setAutoAssign(teamId, v),
      onSuccess: invalidate,
    }),
  };
}

export function useTeamActivity(teamId: Uuid) {
  return useQuery<readonly ActivityEntry[]>({
    queryKey: keys.teamActivity(teamId),
    queryFn: () => fetchTeamActivity(teamId),
  });
}

export function useClientActivity(clientId: Uuid, enabled: boolean) {
  return useQuery<readonly ActivityEntry[]>({
    queryKey: keys.clientActivity(clientId),
    queryFn: () => fetchClientActivity(clientId),
    enabled,
  });
}

export function useAssignConversation(conversationId: Uuid) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (b: AssignRequest) => assignConversation(conversationId, b),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.conversation(conversationId) });
      void client.invalidateQueries({ queryKey: keys.conversations });
    },
  });
}
